// Э-7. Отчёт прораба — телефон, перчатки, плохая связь. Два шага: «Работы» и «Люди и смена».
// Объект, дата и смена подставлены; работы — по плану на сегодня плюс незавершённые вчерашние.
// Без связи отчёт уходит в очередь и отправится сам (ключ идемпотентности не даст дубля).

import { useEffect, useMemo, useRef, useState } from "react";
import { addDays, fmtDate, fmtNum, fmtTime, localISODate } from "../../shared/format";
import { TopBar } from "../../shared/shell";
import { Button, Field, Input, Loading, Select, useAsync, useToast } from "../../shared/ui";
import type { QueuedPhoto } from "../../shared/photos";
import { InstallButton } from "../../shared/install";
import { api, can, floorName, photoQueue, queue, useCtx } from "../ctx";

type Row = {
  task_id: string; floor: number; grip: string; work: string; work_code: string; crew: string; unit: string;
  needs_volume: boolean; planned_percent: number; fact_percent: number; from_yesterday: boolean;
  ahead?: boolean; planned_start?: string; // по плану позже — можно начать раньше плана (встреча 24.10.2026)
  entered_percent: number | null; entered_volume: number | null; planned_volume: number | null; deviation: string;
  prev_percent: number; // процент до этой смены (по прошлым отчётам)
};
type Item = { percent: number | null; volume: string; comment: string; ai?: boolean; photos?: File[] };
type NewIssue = { description: string; floor_number: string; grip_code: string; photos: File[] };

const STEPS = [0, 25, 50, 75, 100];
const MAX_PHOTOS = 10; // ФТ-3.7: до 10 фото к отчёту

export function ReportPage({ id }: { id: string }) {
  const { me } = useCtx();
  const toast = useToast();
  const [when, setWhen] = useState<{ day?: string; shift?: string }>({});
  const f = useAsync<any>(() => api.get(`/buildings/${id}/reports/today`, when), [id, when.day, when.shift]);
  const reasons = useAsync<any[]>(() => api.get("/downtime-reasons"), []);
  // «Завершить день» с главной прораба открывает сразу второй шаг (?step=2)
  const startStep = useRef<1 | 2>(new URLSearchParams(location.hash.split("?")[1] || "").get("step") === "2" ? 2 : 1);
  const [step, setStep] = useState<1 | 2>(startStep.current);
  const [items, setItems] = useState<Record<string, Item>>({});
  const [heads, setHeads] = useState<Record<string, number>>({});
  const [downtime, setDowntime] = useState("0");
  const [reason, setReason] = useState("");
  const [issues, setIssues] = useState<NewIssue[]>([]);
  const [planTomorrow, setPlanTomorrow] = useState("");
  const [comment, setComment] = useState("");
  const [weather, setWeather] = useState("");
  const [temp, setTemp] = useState("");
  const [rawText, setRawText] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiNote, setAiNote] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [correction, setCorrection] = useState("");
  const [errTask, setErrTask] = useState<string | null>(null);
  const [showAhead, setShowAhead] = useState(false);
  const [brigade, setBrigade] = useState(""); // «id|часть» — кто был бригадиром (встреча 24.10.2026)
  const [brigadierName, setBrigadierName] = useState(""); // бригадир не из списка бригад
  const corrector = can(me, "reports.correct");

  // заполнить форму тем, что уже введено в эту смену (или пустыми значениями)
  useEffect(() => {
    const d = f.data;
    if (!d) return;
    const r = d.report;
    const it: Record<string, Item> = {};
    for (const t of d.tasks as Row[]) {
      it[t.task_id] = { percent: t.entered_percent, volume: t.entered_volume != null ? String(t.entered_volume) : "", comment: "" };
    }
    setItems(it);
    const h: Record<string, number> = {};
    for (const c of d.crews) h[c.code] = c.entered ?? c.planned ?? 0;
    setHeads(h);
    setDowntime(String(r?.downtime_hours ?? 0));
    setReason(r?.downtime_reason_id || "");
    setPlanTomorrow(r?.plan_tomorrow || "");
    setComment(r?.comment || "");
    setWeather(r?.weather || "");
    setTemp(r?.temperature_c != null ? String(r.temperature_c) : "");
    const sug = r?.brigade_id ? { brigade_id: r.brigade_id, part: r.brigade_part } : d.brigade_suggested;
    const brs: any[] = d.brigades || [];
    const pick = sug && brs.some((b) => b.id === sug.brigade_id) ? `${sug.brigade_id}|${sug.part || ""}` : brs.length === 1 && brs[0].parts.length === 1 ? `${brs[0].id}|${brs[0].parts[0].key}` : "";
    setBrigade(pick);
    setBrigadierName(r && !r.brigade_id ? r.brigadier || "" : "");
    if (!r && pick) { // новый отчёт: люди по составу бригады
      const [bid, key] = pick.split("|");
      const part = brs.find((b) => b.id === bid)?.parts.find((x: any) => x.key === key);
      if (part) for (const c of d.crews) h[c.code] = part.composition?.[c.code] ?? 0;
      setHeads({ ...h });
    }
    setIssues([]);
    setAiNote(null);
    setStep(startStep.current);
    startStep.current = 1; // переход по ?step=2 — только при первом открытии
  }, [f.data]);

  const rows: Row[] = f.data?.tasks || [];
  const planned = rows.filter((t) => !t.ahead), ahead = rows.filter((t) => t.ahead);
  const aheadFilled = ahead.filter((t) => items[t.task_id]?.percent != null).length;
  const aheadOpen = showAhead || aheadFilled > 0 || ahead.some((t) => t.task_id === errTask);
  const shiftOver = f.data ? new Date(f.data.shift_end) < new Date() : false;
  const submitted = f.data?.report?.status === "submitted";
  const needCorrection = submitted && shiftOver;
  const readOnly = needCorrection && !corrector;
  const filled = rows.filter((t) => items[t.task_id]?.percent != null).length;
  const set = (tid: string, patch: Partial<Item>) => setItems((x) => ({ ...x, [tid]: { ...x[tid], ...patch, ai: "photos" in patch ? x[tid]?.ai : false } }));
  const photoCount = Object.values(items).reduce((n, i) => n + (i.photos?.length || 0), 0) + issues.reduce((n, i) => n + i.photos.length, 0);
  const photoRoom = MAX_PHOTOS - photoCount;

  const missingVolume = rows.filter((t) => t.needs_volume && (items[t.task_id]?.percent ?? 0) >= 100 && !items[t.task_id]?.volume && t.entered_volume == null);
  // ниже процента прошлых смен — только с пояснением (так же проверяет сервер); свою правку в смену можно без него
  const noReason = rows.filter((t) => { const i = items[t.task_id]; return i?.percent != null && i.percent < (t.prev_percent ?? 0) && !i.comment?.trim(); });

  async function applyAi() {
    if (!rawText.trim()) return;
    setBusy(true);
    try {
      const d = await api.post(`/buildings/${id}/ai/parse-report`, { text: rawText, day: f.data.date, shift: f.data.shift });
      setItems((x) => {
        const n = { ...x };
        for (const i of d.items) n[i.task_id] = { ...n[i.task_id], percent: i.percent, volume: i.volume != null ? String(i.volume) : n[i.task_id]?.volume || "", ai: true };
        return n;
      });
      if (Object.keys(d.headcount).length) setHeads((h) => ({ ...h, ...d.headcount }));
      if (d.downtime_hours) { setDowntime(String(d.downtime_hours)); if (d.downtime_reason_id) setReason(d.downtime_reason_id); }
      if (d.plan_tomorrow) setPlanTomorrow(d.plan_tomorrow);
      if (d.issues.length) setIssues((x) => [...x, ...d.issues.map((i: any) => ({ description: i.description, floor_number: i.floor_number ? String(i.floor_number) : "", grip_code: i.grip_code || "", photos: [] }))]);
      setAiNote(d);
      setAiOpen(false);
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  }

  async function save(submit: boolean) {
    const d = f.data;
    const payload = {
      date: d.date, shift: d.shift, submit,
      items: rows.filter((t) => items[t.task_id]?.percent != null).map((t) => {
        const i = items[t.task_id];
        return { task_id: t.task_id, percent: i.percent, volume: i.volume ? Number(i.volume.replace(",", ".")) : null, comment: i.comment || null };
      }),
      headcount: heads,
      downtime_hours: Number(downtime.replace(",", ".")) || 0,
      downtime_reason_id: reason || null,
      issues: issues.filter((i) => i.description.trim()).map((i) => ({ description: i.description.trim(), floor_number: i.floor_number ? Number(i.floor_number) : null, grip_code: i.grip_code || null })),
      plan_tomorrow: planTomorrow || null, comment: comment || null, weather: weather || null,
      temperature_c: temp ? Number(temp.replace(",", ".")) : null,
      raw_text: rawText || null, version: d.report?.version ?? null,
      brigade_id: brigade && brigade !== "other" ? brigade.split("|")[0] : null, brigade_part: brigade && brigade !== "other" ? brigade.split("|")[1] || null : null,
      brigadier_name: !brigade || brigade === "other" ? brigadierName.trim() || null : null,
      correction_reason: needCorrection ? correction : null,
    };
    setBusy(true);
    setErrTask(null);
    try {
      const res: any = await queue.send("POST", `/buildings/${id}/reports`, payload, `Отчёт ${fmtDate(d.date)}, ${d.shift === "day" ? "день" : "ночь"}`);
      // фото — в очередь: сжимаются на телефоне и уходят в фоне; смена привяжет их к отчёту,
      // даже если сам отчёт ещё ждёт связи
      const base = { report_id: res?.id, report_date: d.date, report_shift: d.shift };
      let n = 0;
      for (const t of rows) {
        for (const ph of items[t.task_id]?.photos || []) {
          await photoQueue.add(id, ph, { ...base, task_id: t.task_id, caption: `${t.grip} · ${t.work}` });
          n++;
        }
      }
      for (const i of issues) {
        for (const ph of i.photos) {
          await photoQueue.add(id, ph, { ...base, floor_number: i.floor_number || undefined, grip_code: i.grip_code || undefined, caption: i.description.slice(0, 200) });
          n++;
        }
      }
      setItems((x) => Object.fromEntries(Object.entries(x).map(([k, v]) => [k, { ...v, photos: [] }])));
      setIssues((x) => x.map((i) => ({ ...i, photos: [] })));
      const ph = n ? ` Фото (${n}) загружаются в фоне.` : "";
      if (res === null) {
        toast.ok(`Нет связи: отчёт сохранён на телефоне и отправится сам.${n ? ` Фото (${n}) — тоже, когда появится связь.` : ""}`);
        return;
      }
      toast.ok((submit ? "Отчёт отправлен." : "Черновик сохранён.") + ph);
      window.scrollTo(0, 0);
      document.querySelector(".main")?.scrollTo(0, 0);
      f.reload();
    } catch (e: any) {
      if (e?.details?.task_id) { setErrTask(e.details.task_id); setStep(1); }
      toast.error(e);
    } finally { setBusy(false); }
  }

  const shiftLabel = f.data ? `${fmtDate(f.data.date)}, ${f.data.shift === "day" ? "дневная" : "ночная"} смена` : "";
  return (
    <>
      <TopBar title="Отчёт за смену"><InstallButton name="Приложение «Стройка»" /></TopBar>
      <div className="content">
        {!f.data ? <Loading error={f.error} onRetry={f.reload} /> : (
          <div className="mobile-form stack">
            <div className="panel pad stack tight">
              <div className="row between">
                <div>
                  <h2>{f.data.building.name}</h2>
                  <div className="muted small">{shiftLabel}, до {fmtTime(f.data.shift_end)}</div>
                </div>
                {submitted ? <span className="tag s-done">отправлен</span> : f.data.report ? <span className="tag s-planned">черновик</span> : <span className="tag plain">не заполнен</span>}
              </div>
              <ShiftPicker date={f.data.date} shift={f.data.shift} onChange={setWhen} />
            </div>

            <PhotoQueuePanel buildingId={id} />
            {readOnly ? (
              <div className="alert info"><div className="t">Смена закончилась, отчёт отправлен. Исправить его может руководитель — напишите ему, что нужно поправить.</div></div>
            ) : (
              <>
                <div className="row">
                  <Button className="grow" variant={step === 1 ? "primary" : undefined} aria-pressed={step === 1} onClick={() => setStep(1)}>1. Работы · {filled}/{planned.length}</Button>
                  <Button className="grow" variant={step === 2 ? "primary" : undefined} aria-pressed={step === 2} onClick={() => setStep(2)}>2. Люди и смена</Button>
                </div>

                {step === 1 && (
                  <>
                    {!aiOpen ? (
                      <Button onClick={() => setAiOpen(true)}>Заполнить из текста</Button>
                    ) : (
                      <div className="ai stack tight">
                        <span className="badge">Напишите или надиктуйте отчёт — система разложит его по полям</span>
                        <textarea className="input" rows={4} value={rawText} onChange={(e) => setRawText(e.target.value)}
                          placeholder="С4 демонтаж 100%, П1 арматура 60%. Людей А 8 М 9 П 4. Простой 2 часа — кран. Завтра П1 бетон" />
                        <div className="row"><Button variant="primary" disabled={busy || !rawText.trim()} onClick={applyAi}>Разложить по полям</Button><Button variant="ghost" onClick={() => setAiOpen(false)}>Закрыть</Button></div>
                      </div>
                    )}
                    {aiNote && (
                      <div className="ai small">
                        <b>Черновик заполнен автоматически — проверьте значения, отмеченные «авто».</b>
                        {aiNote.unmatched.length > 0 && <div>Не разобрано: {aiNote.unmatched.join("; ")}</div>}
                      </div>
                    )}
                    {rows.length === 0 && <div className="empty">На эту смену работ по плану нет.</div>}
                    {planned.length === 0 && ahead.length > 0 && <div className="empty">По плану работы начнутся {fmtDate(ahead[0].planned_start!)}. Если уже начали — отметьте ниже, в «Раньше плана».</div>}
                    {planned.map((t) => <WorkCard key={t.task_id} t={t} v={items[t.task_id]} room={photoRoom} error={errTask === t.task_id} onChange={(p) => set(t.task_id, p)} />)}
                    {ahead.length > 0 && (
                      <div className="stack tight">
                        <Button aria-expanded={aheadOpen} onClick={() => setShowAhead(!aheadOpen)}>{aheadOpen ? "Скрыть" : "Раньше плана"} · ближайшие работы: {ahead.length}{aheadFilled ? `, отмечено ${aheadFilled}` : ""}</Button>
                        {aheadOpen && ahead.map((t) => <WorkCard key={t.task_id} t={t} v={items[t.task_id]} room={photoRoom} error={errTask === t.task_id} onChange={(p) => set(t.task_id, p)} />)}
                      </div>
                    )}
                    <div className="sticky-action"><Button variant="primary" size="big" block onClick={() => setStep(2)}>Дальше: люди и смена</Button></div>
                  </>
                )}

                {step === 2 && (
                  <>
                    <div className="panel pad stack tight">
                      <h3>Бригадир на смене</h3>
                      {(f.data.brigades || []).length > 0 && (
                        <Select aria-label="Бригадир на смене" value={brigade} placeholder="Выберите бригадира" onChange={(e) => {
                          setBrigade(e.target.value);
                          const [bid, key] = e.target.value.split("|");
                          const part = (f.data.brigades as any[]).find((b) => b.id === bid)?.parts.find((x: any) => x.key === key);
                          if (part) setHeads(Object.fromEntries((f.data.crews as any[]).map((c) => [c.code, part.composition?.[c.code] ?? 0])));
                        }} options={[...(f.data.brigades as any[]).flatMap((b) => b.parts.map((p: any) => ({ value: `${b.id}|${p.key}`, label: `${p.brigadier} — ${b.name}${b.parts.length > 1 ? `, ${p.title}` : ""}` }))), { value: "other", label: "Другой — вписать фамилию" }]} />
                      )}
                      {((f.data.brigades || []).length === 0 || brigade === "other") && (
                        <Input aria-label="Фамилия бригадира" value={brigadierName} onChange={(e) => setBrigadierName(e.target.value)} placeholder="Фамилия И.О. бригадира" />
                      )}
                      <span className="small muted">{(f.data.brigades || []).length > 0
                        ? "Выберите, кто был бригадиром: численность ниже подставится по составу его бригады — поправьте по факту."
                        : "Бригад объекта пока нет — впишите фамилию. Бригады с бригадирами и составом заводит руководитель: меню «Бригады» → «Бригады и бригадиры»."}</span>
                    </div>
                    <div className="panel pad stack">
                      <h3>Людей на смене</h3>
                      {(f.data.crews as any[]).map((c) => (
                        <div key={c.code} className="row between">
                          <span>{c.name}<div className="muted small">по плану {c.planned ?? "—"}</div></span>
                          <Stepper value={heads[c.code] ?? 0} onChange={(v) => setHeads((h) => ({ ...h, [c.code]: v }))} label={c.name} />
                        </div>
                      ))}
                    </div>
                    <div className="panel pad stack">
                      <h3>Простой</h3>
                      <div className="form-grid">
                        <Field label="Часов"><Input inputMode="decimal" value={downtime} onChange={(e) => setDowntime(e.target.value)} /></Field>
                        <Field label="Причина"><Select value={reason} onChange={(e) => setReason(e.target.value)} placeholder="—" options={(reasons.data || []).map((r) => ({ value: r.id, label: r.name }))} /></Field>
                      </div>
                    </div>
                    <div className="panel pad stack">
                      <div className="row between"><h3>Проблемы</h3><Button size="small" onClick={() => setIssues((x) => [...x, { description: "", floor_number: rows[0] ? String(rows[0].floor) : "", grip_code: "", photos: [] }])}>Добавить</Button></div>
                      {issues.length === 0 && <p className="muted small">Нет материала, сломан кран, не приехал бетон — добавьте, руководитель увидит сразу.</p>}
                      {issues.map((i, n) => <IssueEditor key={n} v={i} room={photoRoom} grips={[...new Set(rows.map((r) => r.grip))]} onChange={(p) => setIssues((x) => x.map((y, k) => (k === n ? { ...y, ...p } : y)))} onRemove={() => setIssues((x) => x.filter((_, k) => k !== n))} />)}
                    </div>
                    <div className="panel pad stack">
                      <div className="form-grid">
                        <Field label="Погода"><Select value={weather} onChange={(e) => setWeather(e.target.value)} placeholder="—" options={["ясно", "облачно", "дождь", "снег", "ветер", "мороз"].map((w) => ({ value: w, label: w }))} /></Field>
                        <Field label="Температура, °C" hint="Важно для набора прочности"><Input inputMode="decimal" value={temp} onChange={(e) => setTemp(e.target.value)} /></Field>
                        <Field label="План на завтра" wide><textarea className="input" rows={2} value={planTomorrow} onChange={(e) => setPlanTomorrow(e.target.value)} /></Field>
                        <Field label="Комментарий" wide><textarea className="input" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} /></Field>
                        {needCorrection && <Field label="Причина исправления" wide hint="Исходные данные сохранятся в истории"><Input value={correction} onChange={(e) => setCorrection(e.target.value)} /></Field>}
                      </div>
                    </div>
                    <div className="muted small">Фото к отчёту: {photoCount} из {MAX_PHOTOS}. Сжимаются на телефоне, уходят в фоне — без связи подождут сети.</div>
                    {missingVolume.length > 0 && <div className="alert"><div className="t">Укажите фактический объём: {missingVolume.map((t) => `${t.grip} · ${t.work}`).join(", ")}</div></div>}
                    {noReason.length > 0 && <div className="alert"><div className="t">Процент меньше, чем в прошлых отчётах, — напишите почему: {noReason.map((t) => `${t.grip} · ${t.work}`).join(", ")}</div></div>}
                    <div className="sticky-action stack tight">
                      <Button variant="primary" size="big" block disabled={busy || noReason.length > 0 || (needCorrection && correction.trim().length < 2)} onClick={() => save(true)}>{submitted ? "Сохранить изменения" : "Отправить отчёт"}</Button>
                      {!submitted && <Button block disabled={busy} onClick={() => save(false)}>Сохранить черновик</Button>}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function ShiftPicker({ date, shift, onChange }: { date: string; shift: string; onChange: (v: { day?: string; shift?: string }) => void }) {
  const today = localISODate();
  const [other, setOther] = useState(false);
  const opts = [addDays(today, -1), today].flatMap((d) => [
    { value: `${d}|day`, label: `${fmtDate(d)}, день` },
    { value: `${d}|night`, label: `${fmtDate(d)}, ночь` },
  ]);
  if (!opts.some((o) => o.value === `${date}|${shift}`)) opts.push({ value: `${date}|${shift}`, label: `${fmtDate(date)}, ${shift === "day" ? "день" : "ночь"}` });
  opts.push({ value: "other", label: "Другая дата…" });
  // встреча 22.10.2026: первые факты (уже сделанные работы) вносятся задним числом
  if (other) {
    return (
      <span className="row tight">
        <Input type="date" aria-label="Дата отчёта" max={today} value={date} onChange={(e) => e.target.value && onChange({ day: e.target.value })} style={{ width: 160 }} />
        <Select aria-label="Смена" value={shift} style={{ width: 110 }} onChange={(e) => onChange({ shift: e.target.value })} options={[{ value: "day", label: "день" }, { value: "night", label: "ночь" }]} />
      </span>
    );
  }
  return (
    <Select aria-label="Смена" value={`${date}|${shift}`} options={opts} onChange={(e) => {
      if (e.target.value === "other") { setOther(true); return; }
      const [d, s] = e.target.value.split("|"); onChange({ day: d, shift: s });
    }} />
  );
}

function WorkCard({ t, v, room, error, onChange }: { t: Row; v: Item | undefined; room: number; error: boolean; onChange: (p: Partial<Item>) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { if (error) ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }, [error]);
  const pct = v?.percent;
  const custom = pct != null && !STEPS.includes(pct);
  const decreased = pct != null && pct < (t.prev_percent ?? 0);
  const [own, setOwn] = useState(false);
  // объём спрашиваем, только когда по работе что-то сделано (обязателен при 100% для бетона и арматуры)
  const needVolume = t.needs_volume && ((pct ?? 0) > 0 || !!v?.volume);
  return (
    <div ref={ref} className="work-card" style={error ? { borderColor: "var(--st-conflict)", borderWidth: 2 } : undefined}>
      <div className="row between">
        <div>
          <b>{t.grip} · {t.work}</b>
          <div className="meta">{floorName(t.floor, false)} · бригада {t.crew || "—"}{t.from_yesterday ? " · со вчера" : ""}{t.ahead && t.planned_start ? ` · по плану с ${fmtDate(t.planned_start)}` : ""}</div>
        </div>
        <div className="right small">
          <div>сейчас {fmtNum(t.fact_percent, 0)}%</div>
          <div className="muted">план {fmtNum(t.planned_percent, 0)}%</div>
        </div>
      </div>
      <div className="seg" role="group" aria-label={`Выполнено, ${t.grip} ${t.work}`}>
        {STEPS.map((s) => <button key={s} type="button" aria-pressed={pct === s} onClick={() => { setOwn(false); onChange({ percent: s }); }}>{s}%</button>)}
      </div>
      {(own || custom || needVolume) && (
        <div className="row">
          {(own || custom) && <Input aria-label="Свой процент" inputMode="numeric" placeholder="свой %" style={{ width: 96 }} autoFocus={own && !custom} value={custom ? String(pct) : ""}
            onChange={(e) => { const n = Number(e.target.value); onChange({ percent: e.target.value === "" ? null : Math.max(0, Math.min(100, n)) }); }} />}
          {needVolume && (
            <Input aria-label={`Объём, ${t.unit}`} inputMode="decimal" placeholder={`объём, ${t.unit}${t.planned_volume ? ` (план ${fmtNum(t.planned_volume)})` : ""}`} className="grow"
              value={v?.volume || ""} onChange={(e) => onChange({ volume: e.target.value })} />
          )}
        </div>
      )}
      <div className="row tight card-links">
        {!own && !custom && <button type="button" className="link" onClick={() => setOwn(true)}>другой %</button>}
        <PhotoPick label="фото" files={v?.photos || []} room={room} onChange={(photos) => onChange({ photos })} />
        {v?.ai && <span className="tag s-planned">заполнено из текста</span>}
      </div>
      <Thumbs files={v?.photos || []} onRemove={(k) => onChange({ photos: (v?.photos || []).filter((_, j) => j !== k) })} />
      {(decreased || v?.comment) && (
        <Input aria-label="Комментарий" placeholder={decreased ? "Процент меньше прежнего — почему?" : "Комментарий"} value={v?.comment || ""} onChange={(e) => onChange({ comment: e.target.value })} />
      )}
    </div>
  );
}

function Stepper({ value, onChange, label }: { value: number; onChange: (v: number) => void; label: string }) {
  return (
    <div className="stepper" role="group" aria-label={label}>
      <button type="button" aria-label="Меньше" onClick={() => onChange(Math.max(0, value - 1))}>−</button>
      <input inputMode="numeric" aria-label={`${label}, человек`} value={value} onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))} />
      <button type="button" aria-label="Больше" onClick={() => onChange(value + 1)}>+</button>
    </div>
  );
}

function IssueEditor({ v, grips, room, onChange, onRemove }: { v: NewIssue; grips: string[]; room: number; onChange: (p: Partial<NewIssue>) => void; onRemove: () => void }) {
  return (
    <div className="work-card">
      <textarea className="input" rows={2} placeholder="Что случилось" value={v.description} onChange={(e) => onChange({ description: e.target.value })} />
      <div className="row">
        <Input aria-label="Этаж" inputMode="numeric" placeholder="этаж" style={{ width: 80 }} value={v.floor_number} onChange={(e) => onChange({ floor_number: e.target.value })} />
        <Select aria-label="Захватка" value={v.grip_code} onChange={(e) => onChange({ grip_code: e.target.value })} placeholder="захватка" options={grips.map((g) => ({ value: g, label: g }))} style={{ width: 120 }} />
        <PhotoPick label="Фото" files={v.photos} room={room} onChange={(photos) => onChange({ photos })} />
        <Button size="small" variant="ghost" onClick={onRemove}>Убрать</Button>
      </div>
      <Thumbs files={v.photos} onRemove={(k) => onChange({ photos: v.photos.filter((_, j) => j !== k) })} />
    </div>
  );
}

/** Одна кнопка: на телефоне система сама предложит камеру или галерею. Лимит — на весь отчёт. */
function PhotoPick({ label, files, room, onChange }: { label: string; files: File[]; room: number; onChange: (f: File[]) => void }) {
  const toast = useToast();
  const add = (list: FileList | null) => {
    const picked = Array.from(list || []).filter((f) => f.type.startsWith("image/") || /\.(jpe?g|png|webp|heic)$/i.test(f.name));
    if (picked.length > room) toast.error(`К отчёту можно приложить не больше ${MAX_PHOTOS} фото`);
    if (room > 0) onChange([...files, ...picked.slice(0, room)]);
  };
  return (
    <label className={`link${room <= 0 ? " disabled" : ""}`} aria-disabled={room <= 0}>+ {label}{files.length ? ` (${files.length})` : ""}
      <input type="file" accept="image/*" multiple hidden disabled={room <= 0} onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
    </label>
  );
}

function Thumbs({ files, onRemove }: { files: File[]; onRemove: (i: number) => void }) {
  const urls = useMemo(() => files.map((p) => URL.createObjectURL(p)), [files]);
  useEffect(() => () => urls.forEach((u) => URL.revokeObjectURL(u)), [urls]);
  if (!urls.length) return null;
  return (
    <div className="row tight">
      {urls.map((u, i) => (
        <button key={u} type="button" className="thumb-x" title="Убрать фото" aria-label="Убрать фото" onClick={() => onRemove(i)}>
          <img src={u} alt="" />
          <span>×</span>
        </button>
      ))}
    </div>
  );
}

/** Фото, которые ещё не ушли: сколько ждёт сети и какие сервер не принял (их можно убрать). */
function PhotoQueuePanel({ buildingId }: { buildingId: string }) {
  const [items, setItems] = useState<QueuedPhoto[]>([]);
  useEffect(() => photoQueue.subscribe((all) => setItems(all.filter((p) => p.buildingId === buildingId))), [buildingId]);
  if (!items.length) return null;
  const failed = items.filter((p) => p.error);
  const waiting = items.length - failed.length;
  return (
    <div className="alert info">
      <div className="t">
        {waiting > 0 && <>Фото ждут отправки: {waiting}. Уйдут сами, когда появится связь. </>}
        {waiting > 0 && navigator.onLine && <Button size="small" onClick={() => void photoQueue.flush()}>Отправить сейчас</Button>}
      </div>
      {failed.map((p) => (
        <div key={p.id} className="row between small">
          <span>{p.meta.caption || p.name}: не принято — {p.error}</span>
          <Button size="small" variant="ghost" onClick={() => void photoQueue.remove(p.id)}>Убрать</Button>
        </div>
      ))}
    </div>
  );
}
